import Chart from "chart.js/auto";
import React, { useState, useEffect } from "react";
import {
  referenceTable,
  referenceEnergies,
  stateIndices,
  formulaLookup,
} from "../ReactionPaths.js";

const CatalystPathPlot = ({ plotId, plotData, reaction, surface, facet }) => {
  var data = plotData;

  function makeChart() {
    if (data.length === 0) return;
    let dataLength = 0.8;
    let sequence = stateIndices[data[0].reaction.reaction_type];
    let pointsNum = sequence.length;

    data.sort(
      (path_a, path_b) => path_a.reaction.potential - path_b.reaction.potential,
    );

    let potentials = data.map((path) => path.reaction.potential);

    function isDashed(ctx) {
      return ctx.p0.parsed.y !== ctx.p1.parsed.y ? [3, 3] : undefined;
    }

    let tickLocs = [];

    var plottingDatasets = potentials.map((potential, potential_i) => {
      var cumuX = 0.0;
      let dataPoints = [];
      sequence.forEach((reaction, reaction_i) => {
        if (reaction_i != 0) cumuX += (1 - dataLength) / (pointsNum - 1);
        dataPoints.push({
          x: cumuX,
          y: data[potential_i].reaction.steps[reaction].energies[0],
        });
        cumuX += dataLength / (2 * pointsNum);
        if (tickLocs.length < sequence.length) {
          tickLocs.push(cumuX);
        }
        dataPoints.push({
          x: cumuX,
          y: data[potential_i].reaction.steps[reaction].energies[0],
        });
        cumuX += dataLength / (2 * pointsNum);
        dataPoints.push({
          x: cumuX,
          y: data[potential_i].reaction.steps[reaction].energies[0],
        });
      });
      let datasetLabels = [];
      sequence.forEach((seqLabel, seq_i) => {
        for (let j = 0; j < 3; j++) {
          let systemLabel = "";
          let systemId = data[potential_i].reaction.steps[seqLabel].systems[0];
          if (seq_i == 0 || seq_i == sequence.length - 1) {
            systemLabel = "/catalysts/" + systemId + ":" + seqLabel;
          } else {
            systemLabel = "/adsorbed_states/" + systemId + ":" + seqLabel;
          }
          datasetLabels.push(systemLabel);
        }
      });
      return {
        label: String(potential) + " V",
        labels: datasetLabels,
        data: dataPoints,
        showLine: true,
        fill: false,
        segment: {
          borderDash: isDashed,
        },
      };
    });

    const chartData = {
      datasets: plottingDatasets,
    };

    function tooltipLabel(context, data) {
      return context.parsed.y.toPrecision(3) + " eV";
    }

    function tooltipTitle(context) {
      let label = context[0].dataset.labels[context[0].dataIndex].split(":")[1];
      return label;
    }

    function tooltipBeforeLabel(context, data) {
      return context.dataset.label;
    }

    const config = {
      type: "scatter",
      data: chartData,
      options: {
        aspectRatio: 2,
        responsive: true,
        elements: {
          point: {
            radius: 0.0,
            hitRadius: 10.0,
          },
        },
        interaction: {
          intersect: false,
          axis: "x",
        },
        scales: {
          x: {
            min: 0.0,
            max: 1.0,
            afterBuildTicks: (axis) =>
              (axis.ticks = tickLocs.map((v) => ({ value: v }))),
            ticks: {
              // For a category axis, the val is the index so the lookup via getLabelForValue is needed
              callback: function (val, index) {
                // Hide every 2nd tick label
                return sequence[index];
              },
              color: "red",
            },
          },
          y: {
            title: {
              display: true,
              text: "Grand Free Energy ΔΦ (eV)",
            },
          },
        },
        plugins: {
          tooltip: {
            callbacks: {
              label: tooltipLabel,
              title: tooltipTitle,
              beforeLabel: tooltipBeforeLabel,
            },
          },
        },
      },
    };
    let ctx = document.getElementById(plotId);
    (async function () {
      new Chart(ctx, config);
    })();
  }

  // Load Chart.js and make initial plot
  useEffect(() => {
    let existingChart = Chart.getChart(plotId);
    if (existingChart?.canvas !== undefined) {
      existingChart.destroy();
    }
    if (data !== undefined) {
      makeChart();
    }
    return;
  }, []);

  function catalystPlotClickHandler(evt) {
    let existingChart = Chart.getChart(plotId);
    const points = existingChart.getElementsAtEventForMode(
      evt,
      "nearest",
      { intersect: true },
      true,
    );

    if (points.length) {
      var firstPoint = points[0];
      // Adsorbed state plots require slightly different logic...
      var label =
        existingChart.data.datasets[firstPoint.datasetIndex].labels[
          firstPoint.index
        ];
      let system = label.split(":")[0];
      window.location.href = system;
    }
  }

  return (
    <canvas
      id={plotId}
      onClick={catalystPlotClickHandler}
    ></canvas>
  );
};

export { CatalystPathPlot };
